import { useState } from 'react';
import { useSimulationStore } from '../../stores/simulationStore';

export default function TransportControls() {
  const {
    state,
    route,
    startSimulation,
    stopSimulation,
    pauseSimulation,
    resumeSimulation,
    setSimulationSpeed,
  } = useSimulationStore();

  const [selectedSpeed, setSelectedSpeed] = useState(1);
  const status = state?.status ?? 'stopped';
  const canStart = route !== null && (status === 'stopped' || status === 'completed');
  const canPause = status === 'running';
  const canResume = status === 'paused';
  const canStop = status === 'running' || status === 'paused';

  const handleSpeedChange = (speed: number) => {
    setSelectedSpeed(speed);
    setSimulationSpeed(speed);
  };

  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="h-20 bg-nautical-panel border-t border-nautical-border px-4 py-3">
      <div className="flex items-center justify-between h-full">
        {/* Transport buttons */}
        <div className="flex items-center space-x-2">
          {/* Reset */}
          <button
            className="transport-btn text-gray-400 hover:text-gray-200"
            onClick={stopSimulation}
            disabled={status === 'stopped'}
            title="Reset"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h12v12H6V6z" />
            </svg>
          </button>

          {/* Play/Pause */}
          {canPause ? (
            <button
              className="transport-btn transport-btn-active text-white"
              onClick={pauseSimulation}
              title="Pause"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            </button>
          ) : canResume ? (
            <button
              className="transport-btn transport-btn-active text-white"
              onClick={resumeSimulation}
              title="Resume"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7L8 5z" />
              </svg>
            </button>
          ) : (
            <button
              className={`transport-btn ${canStart ? 'text-green-400 hover:text-green-300' : 'text-gray-600 cursor-not-allowed'}`}
              onClick={startSimulation}
              disabled={!canStart}
              title={canStart ? 'Start' : 'Set route first'}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7L8 5z" />
              </svg>
            </button>
          )}

          {/* Stop */}
          <button
            className={`transport-btn ${canStop ? 'text-red-400 hover:text-red-300' : 'text-gray-600'}`}
            onClick={stopSimulation}
            disabled={!canStop}
            title="Stop"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h12v12H6V6z" />
            </svg>
          </button>
        </div>

        {/* Progress bar */}
        <div className="flex-1 mx-8">
          <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
            <span>{formatTime(state?.elapsed_time_seconds ?? 0)}</span>
            <span className="text-gray-200 font-medium">
              {(state?.vessel?.progress_percent ?? 0).toFixed(1)}%
            </span>
            <span>
              {state?.vessel?.distance_remaining?.toFixed(1) ?? '--'} nm remaining
            </span>
          </div>
          <div className="progress-bar h-3">
            <div
              className="progress-bar-fill"
              style={{ width: `${state?.vessel?.progress_percent ?? 0}%` }}
            />
          </div>
        </div>

        {/* Speed selector */}
        <div className="flex items-center space-x-1">
          <span className="text-xs text-gray-400 mr-2">Speed:</span>
          {[0.5, 1, 2, 5, 10].map((speed) => (
            <button
              key={speed}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                selectedSpeed === speed
                  ? 'bg-marine-600 text-white'
                  : 'bg-nautical-dark text-gray-400 hover:text-gray-200'
              }`}
              onClick={() => handleSpeedChange(speed)}
            >
              {speed}x
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
